import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useWebSocket from 'react-use-websocket';
import { toast } from 'react-toastify';
import { fetchAuthSession } from 'aws-amplify/auth';
import useReportStore from '../../store/report-store';
import { env } from '../../shared/config/env';

const useAdminReportSocket = () => {
  const { fetchReport, setRetryLoading } = useReportStore();
  const location = useLocation();
  const [socketUrl, setSocketUrl] = useState(null);
  const isReportsPage = location.pathname === '/reports';
  const isAuthed = typeof window !== 'undefined' && !!localStorage.getItem('user_id');

  useEffect(() => {
    if (!isAuthed) {
      setSocketUrl(null);
      return;
    }

    let cancelled = false;
    const resolveUrl = async () => {
      try {
        const { accessToken } = (await fetchAuthSession()).tokens ?? {};
        if (!cancelled && accessToken && env.wsUrl) {
          setSocketUrl(`${env.wsUrl}?token=${encodeURIComponent(accessToken.toString())}`);
        }
      } catch (error) {
        console.error('Failed to resolve websocket token:', error);
      }
    };
    resolveUrl();

    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  const { lastJsonMessage } = useWebSocket(socketUrl, {
    shouldReconnect: () => isAuthed,
    onClose: () => {
      if (!isAuthed) return;
      fetchAuthSession()
        .then(({ tokens } = {}) => {
          const accessToken = tokens?.accessToken;
          if (!accessToken || !env.wsUrl) {
            setSocketUrl(null);
            return;
          }
          const nextUrl = `${env.wsUrl}?token=${encodeURIComponent(accessToken.toString())}`;
          setSocketUrl((prev) => (prev === nextUrl ? prev : nextUrl));
        })
        .catch((error) => {
          console.error('Failed to refresh websocket token:', error);
        });
    },
    heartbeat: {
      message: 'ping',
      returnMessage: 'pong',
      timeout: 5 * 60000,
      interval: 5 * 1000,
    },
  });

  useEffect(() => {
    const handleWebSocketMessage = async () => {
      if (lastJsonMessage === null) return;
      const payload = lastJsonMessage.data || {};
      const reportId = payload.report_id || payload.id;
      if (!reportId || !isReportsPage) return;

      if (payload.status === 'processing') {
        setRetryLoading(reportId, true);
      }

      await fetchReport(reportId);
      const report = useReportStore.getState().report;

      if (payload.status === 'completed' && report && !report.report_fetched) {
        toast.error(`Report #${report.row_number}: ${report.error ? report.error : ''}`, {
          toastId: `toast-${reportId}`,
        });
      }

      if (payload.status !== 'processing') {
        setRetryLoading(reportId, false);
      }
    };

    handleWebSocketMessage();
  }, [lastJsonMessage, setRetryLoading, fetchReport, isReportsPage]);
};

export default useAdminReportSocket;
